/**
 * UGC video cards + compact carousel.
 *
 * <ugc-video-card> — wraps one card rendered by snippets/ugc-video-card.liquid.
 *   - Autoplays muted while at least half visible (unless prefers-reduced-motion).
 *   - Click: first click plays with sound, next click pauses, click again resumes.
 *   - Only one card plays with sound at a time.
 *
 * <ugc-carousel> — scroll-snap track with prev/next arrows, used by the
 *   ugc-video-carousel theme block (the section uses the native slideshow).
 */
(() => {
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const UNMUTE_EVENT = 'ugc:unmute';

  if (!customElements.get('ugc-video-card')) {
    class UGCVideoCard extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        this.toggle = this.querySelector('[data-ugc-toggle]');
        if (!this.video) return;

        this.userPaused = false;
        this.video.muted = true;

        this.onToggle = this.onToggle.bind(this);
        this.onOtherUnmute = this.onOtherUnmute.bind(this);
        this.toggle?.addEventListener('click', this.onToggle);
        document.addEventListener(UNMUTE_EVENT, this.onOtherUnmute);

        this.video.addEventListener('play', () => this.classList.add('is-playing'));
        this.video.addEventListener('pause', () => this.classList.remove('is-playing'));

        if (this.hasAttribute('autoplay-in-view') && !REDUCED_MOTION.matches) {
          this.observer = new IntersectionObserver(
            ([entry]) => {
              if (entry.isIntersecting) {
                if (!this.userPaused) this.play();
              } else {
                this.video.pause();
                this.mute();
              }
            },
            { threshold: 0.5 }
          );
          this.observer.observe(this);
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        document.removeEventListener(UNMUTE_EVENT, this.onOtherUnmute);
      }

      play() {
        this.video.play().catch(() => this.classList.remove('is-playing'));
      }

      mute() {
        this.video.muted = true;
        this.classList.remove('is-unmuted');
      }

      onToggle(event) {
        event.preventDefault();
        event.stopPropagation();

        if (this.video.paused || this.video.muted) {
          this.video.muted = false;
          this.classList.add('is-unmuted');
          this.userPaused = false;
          document.dispatchEvent(new CustomEvent(UNMUTE_EVENT, { detail: { source: this } }));
          this.play();
        } else {
          this.userPaused = true;
          this.video.pause();
        }
      }

      onOtherUnmute(event) {
        if (event.detail.source === this) return;
        if (!this.video.muted) {
          this.mute();
          // Keep it playing silently if it's an autoplay card, otherwise stop.
          if (!this.hasAttribute('autoplay-in-view')) this.video.pause();
        }
      }
    }

    customElements.define('ugc-video-card', UGCVideoCard);
  }

  if (!customElements.get('ugc-carousel')) {
    class UGCCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-ugc-track]');
        this.prev = this.querySelector('[data-ugc-prev]');
        this.next = this.querySelector('[data-ugc-next]');
        if (!this.track) return;

        this.update = this.update.bind(this);
        this.prev?.addEventListener('click', () => this.scrollByCard(-1));
        this.next?.addEventListener('click', () => this.scrollByCard(1));
        this.track.addEventListener('scroll', this.update, { passive: true });

        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this.track);
        this.update();
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
      }

      cardStep() {
        const card = this.track.firstElementChild;
        if (!card) return this.track.clientWidth;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        return card.getBoundingClientRect().width + gap;
      }

      scrollByCard(direction) {
        this.track.scrollBy({ left: direction * this.cardStep(), behavior: 'smooth' });
      }

      update() {
        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        const scrollable = scrollWidth > clientWidth + 1;
        this.classList.toggle('is-scrollable', scrollable);
        if (this.prev) this.prev.disabled = scrollLeft <= 1;
        if (this.next) this.next.disabled = scrollLeft + clientWidth >= scrollWidth - 1;
      }
    }

    customElements.define('ugc-carousel', UGCCarousel);
  }
})();
